/**
 * Plan 2026-09-27-006 U7a — the one fetch-layer entry point every in-play
 * write that needs OWNERSHIP first goes through (Key Technical Decision 3):
 * the ownership middleware 422s a write to a Library identity carrying
 * `?playId=`, so a caller must ensure the play owns a copy BEFORE issuing
 * that write.
 *
 * Reads the play-scoped library (`libraryQueryKey(playId)`) off the SAME
 * `QueryClient` cache the shell's own `usePlaybooksLibrary`-shaped read
 * already populated, via a synchronous `getQueryData` — never a forced
 * `fetchQuery` round trip — rather than taking a `library` argument, so no
 * caller needs a new prop threaded down just to reach this (Deferred to
 * Implementation default, U7a plan section). `fetchQuery` is the fallback
 * ONLY for the cold-cache case (no entry yet): the app's default
 * `staleTime: 0` (`main.tsx`) means `fetchQuery` treats ANY cached entry as
 * stale and re-fetches unconditionally, which would turn every sole-use
 * settings write (rename, sender, flow id, inflection) into an extra,
 * failure-prone network round trip purely to confirm ownership the shell's
 * own render already proved fresh enough to act on.
 *
 * Every in-play fork names the group (rung) being edited and copies for that
 * group ONLY (plan 2026-09-28-006 U3a, rulebook R3) — a play may hold several
 * copies of one Library piece, one per edited group. Two cases:
 *  1. `mode: 'ensure'` (the default) and `identityId` already IS this play's
 *     own row (a copy or a play-created original) — return it, no request.
 *     A Library id is NEVER swapped for a copy of it this play already holds
 *     (that other copy belongs to other groups; R11 — no substitution).
 *  2. Otherwise — a Library id, or `mode: 'fork'` on an owned id ("Save as
 *     new" on a copy two groups share: a copy of the copy) — `POST
 *     /plays/:id/fork` with `rungId` (Key Technical Decision 1), then
 *     {@link adoptPlayLadderMoves} (one move) BEFORE returning: the rewritten `ladder` (only
 *     that group moved onto the new copy) lands in the playbook-draft store
 *     and the play-scoped playbook-detail cache, THAT group's unsaved copy
 *     drafts and staged AI reviews are rekeyed from `identityId` onto the
 *     copy (`rungDraftKey(rungId)` scope only), and the play's own library
 *     is invalidated so every popover's next read sees the copy's ownership
 *     fields. Callers rely on all of that having happened by the time this
 *     resolves.
 *
 *     Plan 2026-09-28-003 — an optional {@link EnsurePlayOwnedParams.beforeAdopt}
 *     hook runs AFTER the fork response resolves but BEFORE the adopt
 *     repoints the rung: a segmentation save needs the copy's head to already
 *     BE the rep's conditions, and the client to already hold that head in
 *     the segmentation-detail cache, before the rung (and the still-mounted
 *     editor keyed to it) retargets onto the copy — otherwise the editor's
 *     `useDraftState` reseeds from the copy's stale pre-edit head and the
 *     rep's edit appears lost (see `savePlaySegmentationConditions` below and
 *     plans/2026-09-28-003-pr1694-segment-save-as-new-stale-ui-plan.md). Wrapped
 *     in `try { await beforeAdopt(owned) } finally { await adoptPlayLadderMoves(...) }`
 *     — the `finally` is load-bearing: the fork already moved the rung
 *     server-side, so the client ladder must be adopted even when the
 *     follow-up write fails (otherwise the tab is stale and every retry 409s
 *     forever — the class plan 2026-09-27-007 U5 A4 P2 closed).
 *
 * Plan 2026-09-28-007 U5 (R7) — {@link adoptPlayLadderMoves} is the ONE
 * adopt of a rewritten ladder (a fork, a copy save, a fan-out, the rep's
 * Reload — every group that moved, in one call).
 *
 * Plan 2026-09-28-008 U2 (R14) — and a refusal is never recovered from here.
 * A 409 `version_conflict` (another editor or the AI already moved the group)
 * is rethrown untouched; {@link gateInPlayWrite}'s `'stale'` issues no
 * request. The host parks its Save panel stale holding the rep's draft, and
 * the only refetch is the rep's own "Reload to see their change"
 * (`usage-sidebar-reload.ts` → {@link adoptFreshPlayLadder}).
 */
import type { QueryClient } from '@tanstack/react-query';

import { getLibrary, getPlaybook, getSegmentation } from './api-client';
import { clearGeneratedCopyForSequence, rekeyGeneratedCopyForRung } from './generated-copy-store';
import { forkIntoPlay, saveSegmentationConditions } from './mutations';
import { rungDraftKey } from './playbook-draft';
import { adoptSavedPlaybookDraft, usePlaybookDraftStore } from './playbook-draft-store';
import { applyLadderSaveToCache } from './playbook-query-cache';
import { isOwnedByPlay, type PlaybooksOwnershipRow } from './play-ownership';
import {
  libraryQueryKey,
  matchedMembersPrefixKey,
  segmentationDetailQueryKey,
  sequenceDetailQueryKey,
} from './query-keys';
import type {
  PlaybookRung,
  PlaybooksLibraryResponse,
  PlaybooksSegmentationSummary,
  PlaybooksSequenceSummary,
  PlaybooksUpdatePlaysResponse,
  PlaybooksUsagePlace,
  SegmentationConditions,
} from './schemas';
import { clearCopyDraftsForSequence, rekeyCopyDraftsForRung } from './sequence-copy-draft-store';
import { SAVE_GROUP_FIRST } from './sequence-card-menu';
import { signalLadderMovesAdopted } from './ladder-adoption-signal';
import { distinctUnsentPlays, inPlayUsageGate, libraryUsageGateNeedsConfirmation } from './usage-gate';
import type { UsageSidebarOrigin } from './usage-sidebar-store';

/** The injected throwing `apiFetch` (`_shared/hooks/useApi.ts`), never imported here. */
type ApiFetch = (input: RequestInfo, init?: RequestInit) => Promise<Response>;

/**
 * Plan 2026-09-28-006 U3a — the synchronous play-scoped library cache read
 * {@link ensurePlayOwned} does, exported for hosts that need a row's
 * `owningPlayId` / `forkSourceId` / `archivedAt` at open time (e.g. to set
 * the usage sidebar's `target.owningPlayId` / `target.archivedInLibrary`).
 * `undefined` when the play's library is not cached yet or has no such row —
 * never a fetch.
 */
export function readPlayLibraryRow(
  queryClient: QueryClient,
  playId: string,
  kind: 'segmentation',
  id: string,
): PlaybooksSegmentationSummary | undefined;
export function readPlayLibraryRow(
  queryClient: QueryClient,
  playId: string,
  kind: 'sequence',
  id: string,
): PlaybooksSequenceSummary | undefined;
export function readPlayLibraryRow(
  queryClient: QueryClient,
  playId: string,
  kind: 'segmentation' | 'sequence',
  id: string,
): PlaybooksSegmentationSummary | PlaybooksSequenceSummary | undefined;
export function readPlayLibraryRow(
  queryClient: QueryClient,
  playId: string,
  kind: 'segmentation' | 'sequence',
  id: string,
): PlaybooksSegmentationSummary | PlaybooksSequenceSummary | undefined {
  const library = queryClient.getQueryData<PlaybooksLibraryResponse>(libraryQueryKey(playId));
  if (library === undefined) return undefined;
  return kind === 'segmentation'
    ? library.segmentations.find((row) => row.id === id)
    : library.sequences.find((row) => row.id === id);
}

export interface EnsurePlayOwnedParams {
  playId: string;
  playbookId: string;
  kind: 'segmentation' | 'sequence';
  identityId: string;
  /** The group (rung) being edited — the fork copies for this one group only (R3). */
  rungId: string;
  /**
   * `'ensure'` (default): an id this play already owns is returned as-is (no
   * request); a Library id is forked for `rungId`. `'fork'`: always a fresh
   * copy for `rungId`, even of an owned copy (copy-of-copy — "Save as new"
   * on a copy two groups share).
   */
  mode?: 'ensure' | 'fork';
  /** The Library head the caller composed against — a moved head is a 409 `version_conflict` on the fork. */
  expectedSourceVersionId?: string;
  /**
   * Plan 2026-09-28-003 — see this file's header doc. Runs only on the
   * fork path (case 2); case 1 never forks, so there is nothing to
   * order a write against.
   */
  beforeAdopt?: (owned: EnsurePlayOwnedResult) => Promise<void>;
}

export interface EnsurePlayOwnedResult {
  /** The play's own identity — `identityId` unchanged (case 1) or the new copy (case 2). */
  ownedId: string;
  /** The owned identity's head — the next write's `expectedVersionId`. */
  headVersionId: string;
}

export async function ensurePlayOwned(
  apiFetch: ApiFetch,
  queryClient: QueryClient,
  params: EnsurePlayOwnedParams,
): Promise<EnsurePlayOwnedResult> {
  const { playId, playbookId, kind, identityId, rungId, mode = 'ensure', expectedSourceVersionId, beforeAdopt } = params;
  const cached = queryClient.getQueryData<PlaybooksLibraryResponse>(libraryQueryKey(playId));
  const library: PlaybooksLibraryResponse =
    cached ??
    (await queryClient.fetchQuery<PlaybooksLibraryResponse>({
      queryKey: libraryQueryKey(playId),
      queryFn: () => getLibrary(apiFetch, { playId }),
    }));
  // Typed down to the shared ownership shape: `isOwnedByPlay` is generic over
  // ONE `T`, and a plain `kind === 'segmentation' ? ... : ...` ternary's
  // inferred type is a union of the two DIFFERENTLY-shaped summary arrays.
  const rows: readonly (PlaybooksOwnershipRow & { headVersionId: string | null })[] =
    kind === 'segmentation' ? library.segmentations : library.sequences;

  const addressed = rows.find((row) => row.id === identityId);
  if (mode !== 'fork' && addressed && isOwnedByPlay(addressed, playId) && addressed.headVersionId !== null) {
    return { ownedId: addressed.id, headVersionId: addressed.headVersionId };
  }

  const response = await forkIntoPlay(apiFetch, playId, {
    kind,
    identityId,
    rungId,
    ...(expectedSourceVersionId === undefined ? {} : { expectedSourceVersionId }),
  });
  const owned: EnsurePlayOwnedResult = { ownedId: response.identityId, headVersionId: response.headVersionId };
  try {
    if (beforeAdopt) await beforeAdopt(owned);
  } finally {
    await adoptPlayLadderMoves(queryClient, {
      playbookId,
      playId,
      kind,
      ladder: response.ladder,
      moves: [{ rungId, fromId: identityId, toId: response.identityId }],
      drafts: 'carry',
    });
  }
  return owned;
}

export interface SavePlaySegmentationConditionsParams {
  playId: string;
  playbookId: string;
  /** The id the editor addressed — a Library segmentation or one this play already owns. */
  segmentationId: string;
  /** The group being edited — a fork copies for this group only. */
  rungId: string;
  /** Passed through to {@link ensurePlayOwned}; `'fork'` = "Save as new" onto a fresh copy for this group. */
  mode?: 'ensure' | 'fork';
  conditions: SegmentationConditions;
  /** The head the draft was composed against — the Library head, or the owned head. */
  expectedVersionId: string;
  /** `1` on the sole-use path; `pending.places.length` from the usage sidebar. */
  confirmedPlaceCount: number;
  /**
   * Plan 2026-09-29-001 U1 (rulebook R3/R5/R14) — every group of THIS play
   * sharing the addressed piece, confirmed against what the rep was shown
   * (`[rungId]` on the sole-use path with no fan-out to confirm; a fresh
   * fork's own `[rungId]`, since only the forking group can share a copy
   * that new). Threaded onto both inner writes' `group.confirmedRungIds` so
   * a group that started sharing the piece since the rep's usage read is
   * refused `shared_mapping_conflict` rather than silently written over.
   */
  confirmedRungIds: string[];
}

export interface SavePlaySegmentationConditionsResult {
  ownedId: string;
  versionId: string;
}

/**
 * Plan 2026-09-28-003 — the ONE place a play-mode segmentation-conditions
 * save goes through, closing the "stale UI after fork" class this plan
 * fixes: `QualifyStep.tsx#handlePlaySegmentationSaveNew` (the sidebar's
 * "Save as new segment") and `SegmentationConditionsEditor.tsx`'s sole-use
 * silent-copy branch both used to call `ensurePlayOwned` then
 * `saveSegmentationConditions` as two separate, unordered steps — the fork's
 * own adopt repointed the rung onto the copy (and the still-mounted
 * editor reseeded its draft from the copy's HEAD) before the conditions
 * write had even been issued, so the editor showed the copy's pre-edit
 * (Library) conditions with Save lit up as unsaved.
 *
 * Fix: the conditions write, and priming the copy's own detail cache with
 * the WRITTEN head, both run inside {@link ensurePlayOwned}'s `beforeAdopt`
 * hook — i.e. before the rung (and the editor keyed to it) ever retargets
 * onto the copy. By the time the rung repoints, `segmentationDetailQueryKey`
 * already holds the rep's conditions, so the editor's key-change reseed
 * (`useDraftState`) picks them up synchronously — no spinner, no dirty
 * flash, `dirty` computes `false` immediately.
 *
 * Case 1 (already owned, `mode` not `'fork'`) never forks, so there is no
 * repoint to order against — `beforeAdopt` is never invoked for them, and
 * the write runs directly after `ensurePlayOwned` resolves instead.
 *
 * Plan 2026-09-28-008 U2 (R14) — a `version_conflict` from the fork or the
 * write is simply rethrown: nothing here reloads or adopts on a refusal (a
 * fork that DID land is still adopted by `ensurePlayOwned`'s `finally`). The
 * caller parks its Save panel stale with the server's sentence; only the
 * rep's Reload refetches.
 */
export async function savePlaySegmentationConditions(
  apiFetch: ApiFetch,
  queryClient: QueryClient,
  params: SavePlaySegmentationConditionsParams,
): Promise<SavePlaySegmentationConditionsResult> {
  const { playId, playbookId, segmentationId, rungId, mode, conditions, expectedVersionId, confirmedPlaceCount, confirmedRungIds } = params;
  let result: SavePlaySegmentationConditionsResult | undefined;
  async function write(owned: EnsurePlayOwnedResult): Promise<void> {
    const saved = await saveSegmentationConditions(
      apiFetch,
      owned.ownedId,
      conditions,
      owned.headVersionId,
      confirmedPlaceCount,
      { playId, group: { rungId, confirmedRungIds } },
    );
    // Primes the copy's detail cache with the head just written — the same
    // GET the editor would issue on retarget anyway; it just happens before
    // the repoint instead of after, so the editor's key-change reseed reads
    // correct data instantly instead of the copy's stale pre-edit head.
    await queryClient.fetchQuery({
      queryKey: segmentationDetailQueryKey(owned.ownedId),
      queryFn: () => getSegmentation(apiFetch, owned.ownedId),
    });
    result = { ownedId: owned.ownedId, versionId: saved.versionId };
  }
  let wrote = false;
  const owned = await ensurePlayOwned(apiFetch, queryClient, {
    playId,
    playbookId,
    kind: 'segmentation',
    identityId: segmentationId,
    rungId,
    ...(mode === undefined ? {} : { mode }),
    expectedSourceVersionId: expectedVersionId,
    beforeAdopt: async (o) => {
      await write(o);
      wrote = true;
    },
  });
  if (!wrote) {
    await write(owned);
  }
  // Same invalidation set both callers already ran; the adopt (inside
  // `ensurePlayOwned`) already invalidated the library once on the fork
  // path, so the library invalidation here is a harmless repeat that keeps
  // behaviour identical for the no-fork case 1.
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: libraryQueryKey(playId) }),
    queryClient.invalidateQueries({ queryKey: matchedMembersPrefixKey() }),
  ]);
  return result!;
}

/** One group (rung) of the play whose slot moved from `fromId` onto `toId`. */
export interface PlayLadderMove {
  rungId: string;
  fromId: string;
  toId: string;
}

export interface AdoptPlayLadderMovesParams {
  playbookId: string;
  playId: string;
  kind: 'segmentation' | 'sequence';
  /** The rewritten play ladder, `null` when the server returned none (nothing to adopt). */
  ladder: { versionId: string; rungs: PlaybookRung[] } | null;
  /** Every group this write moved — `[]` is a legal ladder-only adopt. */
  moves: readonly PlayLadderMove[];
  /**
   * What happens to each moved group's unsaved typing and staged AI review:
   * `'carry'` moves them onto the group's new copy (the rep keeps typing
   * where they were); `'clear'` drops them — a write that REPLACED the steps
   * (a Gong rebind fan-out) leaves nothing a draft could still apply to, the
   * same identity-level clear the mapping drawer's `onLoaded` does.
   */
  drafts: 'carry' | 'clear';
}

/**
 * Plan 2026-09-28-007 U5 (rulebook R7; replaces the per-group
 * `adoptPlayFork`) — the ONE place a rewritten play ladder is adopted on the
 * client, whichever write produced it (`POST /plays/:id/fork` via
 * {@link ensurePlayOwned}, the copy save in `SequencesStep.tsx`, the
 * `update-plays` fan-out via {@link adoptUpdatePlaysResult}, or the rep's
 * Reload via {@link adoptFreshPlayLadder}), and however many groups it moved:
 *
 *  1. `ladder-adoption-signal.ts` is told the WHOLE set of moves first, in
 *     THIS kind's own slot, so every moved group's editor (`SequenceEditorShell`
 *     for a sequence, `SegmentationConditionsEditor` for a segment) reads its
 *     own `(fromId, toId)` as an adopted move, not a rep-picked swap — the
 *     old one-adopt-per-group left only the last group's pair in the slot,
 *     closing the origin group's Steps (reflection r-4, Codex b1-3), and the
 *     old sequence-only signal let an adopted SEGMENT move reseed the filter
 *     editor out from under the rep (plan 2026-09-29-001 U2, Codex b1);
 *  2. per move, that group's (`rungDraftKey(rungId)`) drafts and staged
 *     reviews are rekeyed onto ITS copy (`'carry'`), or cleared (`'clear'`)
 *     — every other group keeps what it addresses (a fork is per group, R3);
 *  3. the ladder is adopted ONCE — the playbook-draft store (it never
 *     reseeds an open draft on its own) and the play-scoped playbook-detail
 *     cache — and the play's library invalidated ONCE, so the copies'
 *     ownership fields reach every reader.
 *
 * The rekey runs BEFORE the ladder lands, so when a group's editor
 * retargets onto its copy the drafts are already under the copy's key.
 * Callers that clear just-saved steps do so under the COPY key afterwards.
 */
export async function adoptPlayLadderMoves(queryClient: QueryClient, params: AdoptPlayLadderMovesParams): Promise<void> {
  const { playbookId, playId, kind, ladder, drafts } = params;
  const moves = params.moves.filter((move) => move.fromId !== move.toId);
  signalLadderMovesAdopted(kind, moves);
  if (kind === 'sequence') {
    for (const { rungId, fromId, toId } of moves) {
      const scope = rungDraftKey(rungId);
      rekeyCopyDraftsForRung(scope, fromId, toId);
      rekeyGeneratedCopyForRung(scope, fromId, toId);
      if (drafts === 'clear') {
        clearCopyDraftsForSequence(toId);
        clearGeneratedCopyForSequence(toId);
      }
    }
  }
  if (ladder !== null) {
    adoptSavedPlaybookDraft(playbookId, ladder, playId);
    // Also the one library invalidation: a play-scoped ladder save
    // invalidates `libraryQueryKey(playId)` itself.
    await applyLadderSaveToCache(
      queryClient,
      playbookId,
      { versionId: ladder.versionId, versionNumber: null, rungs: ladder.rungs },
      playId,
    );
  } else {
    await queryClient.invalidateQueries({ queryKey: libraryQueryKey(playId) });
  }
}

export interface AdoptUpdatePlaysResultParams {
  playbookId: string;
  /** The ORIGIN play — the one this tab is editing. */
  playId: string;
  kind: 'segmentation' | 'sequence';
  /** The Library identity the fan-out addressed. */
  fromId: string;
  result: PlaybooksUpdatePlaysResponse;
  /** See {@link AdoptPlayLadderMovesParams.drafts}; default `'carry'`. */
  drafts?: 'carry' | 'clear';
}

/**
 * Plan 2026-09-28-006 U3a (rulebook R8) — adopts option ②'s ("Update N
 * plays") response on the client. Only the ORIGIN play's entry matters here:
 * this tab holds that play's draft ladder and drafts; every other play's
 * entry is left alone (no other play's ladder is held in this tab — their
 * caches refetch on their own open).
 *
 * Plan 2026-09-28-007 U5 — one {@link adoptPlayLadderMoves} with every group
 * the origin entry moved (each onto ITS copy); no origin entry is a
 * ladder-free adopt that still invalidates the play's library.
 */
export async function adoptUpdatePlaysResult(
  queryClient: QueryClient,
  params: AdoptUpdatePlaysResultParams,
): Promise<void> {
  const { playbookId, playId, kind, fromId, result, drafts = 'carry' } = params;
  const origin = result.updatedPlays.find((entry) => entry.playId === playId);
  await adoptPlayLadderMoves(queryClient, {
    playbookId,
    playId,
    kind,
    ladder: origin?.ladder ?? null,
    moves: (origin?.rungs ?? []).map((moved) => ({ rungId: moved.rungId, fromId, toId: moved.copyId })),
    drafts,
  });
}

/** Which play's groups to re-read on the rep's Reload: the play, the kind of piece, and every id the refused write addressed. */
export interface StaleLadderContext {
  playbookId: string;
  playId: string;
  kind: 'segmentation' | 'sequence';
  /**
   * The ids whose detail the caller reads — the ones the refused write
   * addressed. Plan 2026-09-29-001 U2: these decide which details to
   * INVALIDATE, never which groups' typing to carry (every moved group's is
   * carried — see {@link freshLadderMoves}).
   */
  identityIds: string[];
}

/** The slot a rung holds for `kind` — its segment, or its sequence. */
function slotOf(rung: Pick<PlaybookRung, 'segmentationId' | 'mapping'>, kind: StaleLadderContext['kind']): string | undefined {
  const id = kind === 'segmentation' ? rung.segmentationId : rung.mapping?.sequenceId;
  return id ?? undefined;
}

/**
 * Plan 2026-09-28-008 U1 — the groups whose `kind` slot moved between the
 * ladder this tab holds and a fresh one. A held group missing from the fresh
 * ladder is not a move (it is gone — nothing to carry onto).
 *
 * Plan 2026-09-29-001 U2 (R14; Codex round-4 B2) — EVERY moved group, not
 * only the ones whose slot happened to be an id the refused write addressed.
 * The old `identityIds` filter meant Reload adopted the whole fresh ladder
 * while carrying only the addressed group's typing: a second group that had
 * also been moved (someone copied both) came back looking as though the rep
 * had never typed in it, its draft stranded under the piece that group no
 * longer points at. Adoption follows the group, so typing follows the group.
 */
export function freshLadderMoves(
  heldRungs: readonly (Pick<PlaybookRung, 'segmentationId' | 'mapping'> & { id?: string })[],
  freshRungs: readonly PlaybookRung[],
  kind: StaleLadderContext['kind'],
): PlayLadderMove[] {
  const freshById = new Map(freshRungs.map((rung) => [rung.id, rung]));
  const moves: PlayLadderMove[] = [];
  for (const held of heldRungs) {
    const fromId = slotOf(held, kind);
    if (held.id === undefined || fromId === undefined) continue;
    const freshRung = freshById.get(held.id);
    const toId = freshRung === undefined ? undefined : slotOf(freshRung, kind);
    if (toId === undefined || toId === fromId) continue;
    moves.push({ rungId: held.id, fromId, toId });
  }
  return moves;
}

/**
 * Plan 2026-09-28-008 U1 (rulebook R14; the old automatic stale-ladder recovery, renamed
 * and made Reload-only by U2)
 * — re-reads the play's groups and adopts them. Runs ONLY when the rep asks
 * ("Reload to see their change", `usage-sidebar-reload.ts`, its one caller):
 * a refusal never reloads on its own.
 *
 *  1. refetches `getPlaybook(playbookId, { playId })` — the play's own ladder;
 *  2. {@link freshLadderMoves} — EVERY held group whose `kind` slot now
 *     holds a different id, not only the ones the refused write addressed
 *     (plan 2026-09-29-001 U2, R14: a group the rep never saved from can
 *     have been moved too, and its unsaved typing must follow it);
 *  3. one {@link adoptPlayLadderMoves} with those moves (`'carry'` — the
 *     rep's typing follows the group onto whatever it now points at; a
 *     position that no longer exists there simply isn't shown), then
 *     invalidates the detail of every id involved.
 *
 * Returns the fresh ladder (`null` when the play has none) so the caller can
 * resolve what its group points at now. THROWS on a failed read — the
 * caller decides what a failure means (Reload keeps the stale state).
 */
export async function adoptFreshPlayLadder(
  apiFetch: ApiFetch,
  queryClient: QueryClient,
  params: StaleLadderContext,
): Promise<{ versionId: string; rungs: PlaybookRung[] } | null> {
  const { playbookId, playId, kind, identityIds } = params;
  const state = usePlaybookDraftStore.getState();
  const heldRungs = state.playbookId === playbookId && state.playId === playId ? (state.draft?.rungs ?? []) : [];
  const fresh = await getPlaybook(apiFetch, playbookId, { playId });
  const involved = new Set(identityIds);
  let head: { versionId: string; rungs: PlaybookRung[] } | null = null;
  if (fresh.head) {
    head = { versionId: fresh.head.versionId, rungs: fresh.head.rungs };
    const moves = freshLadderMoves(heldRungs, fresh.head.rungs, kind);
    for (const move of moves) involved.add(move.toId);
    await adoptPlayLadderMoves(queryClient, { playbookId, playId, kind, ladder: head, moves, drafts: 'carry' });
  } else {
    await queryClient.invalidateQueries({ queryKey: libraryQueryKey(playId) });
  }
  const detailKey = kind === 'segmentation' ? segmentationDetailQueryKey : sequenceDetailQueryKey;
  await Promise.all([...involved].map((id) => queryClient.invalidateQueries({ queryKey: detailKey(id) })));
  return head;
}

export interface GateInPlayWriteParams {
  places: readonly PlaybooksUsagePlace[];
  /** Where the save was composed from — every doorway `saveWithGate`/`saveWithGate`-alike hosts already carry. */
  origin: UsageSidebarOrigin;
}

/**
 * Plan 2026-09-28-007 U5 (REVIEW-THESE follow-up) — the ONE client gate every
 * in-play/in-playbook content and settings save goes through, replacing five
 * hand-copied call sites (`SequencesStep.tsx#saveTemplateWithGate` and
 * `#savePlayWithGate`, `SequenceSenderPopover.tsx#saveSequenceSettingsWithGate`,
 * `SegmentationConditionsEditor.tsx#saveWithGate`,
 * `SequenceMappingDrawer.tsx`), two of which had already drifted (the `===
 * 1` vs `<= 1` bug fixed alongside this helper — see this module's own
 * history).
 *
 *  - `'library'` origin — no rung/group of its own to go stale (R14's own
 *    doc): {@link libraryUsageGateNeedsConfirmation} decides, straight
 *    through, no stale-origin concept at all.
 *  - `'play'` origin with `rungId === null` — a rung staged this session with
 *    no server id yet cannot be matched against `places`, and (unlike the
 *    playbook doorway) an in-play write needs a REAL `rungId` to fork a copy
 *    for this group — throws {@link SAVE_GROUP_FIRST} before asking the gate
 *    a question it can't answer.
 *  - `'playbook'` origin with `rungId === null` — this write always targets
 *    the Library identity directly (never a specific rung's own copy), so a
 *    staged/unsaved rung has nothing to compare against and nothing to
 *    mis-save onto: falls back to the plain count-only classification
 *    (`places.length <= 1 && distinctUnsentPlays(places) <= 1`), the same
 *    threshold `SegmentationConditionsEditor`'s twin fallback already used.
 *  - otherwise — {@link inPlayUsageGate} classifies by the origin GROUP
 *    (rulebook R5, R14). Its `'stale-origin'` (the group the save was
 *    composed from is gone from `places` — moved, remapped or removed since
 *    the rep loaded the page; in a play, also an empty list) is returned as
 *    `'stale'`.
 *
 * Plan 2026-09-28-008 U2 (rulebook R14) — `'stale'` does NOTHING here: no
 * request, no ladder adopt, no cache invalidation, no throw. The host opens
 * the Save panel already stale — the same `openUsageSidebar` input its
 * `'menu'` branch would open, plus `staleRefusal: GROUP_MOVED_SENTENCE`
 * (`usage-gate.ts`) — so
 * the rep's draft stays on screen and on the pending, and only the rep's
 * "Reload to see their change" (`usage-sidebar-reload.ts`) refetches. That
 * is also why this is synchronous and takes no `apiFetch`/`QueryClient`:
 * the gate cannot reload even by accident.
 */
export function gateInPlayWrite(params: GateInPlayWriteParams): 'silent' | 'menu' | 'stale' {
  const { places, origin } = params;
  if (origin.kind === 'library') {
    return libraryUsageGateNeedsConfirmation(places) ? 'menu' : 'silent';
  }
  if (origin.rungId === null) {
    if (origin.kind === 'play') throw new Error(SAVE_GROUP_FIRST);
    return places.length <= 1 && distinctUnsentPlays(places) <= 1 ? 'silent' : 'menu';
  }
  const gate = inPlayUsageGate(places, {
    playbookId: origin.playbookId,
    rungId: origin.rungId,
    playId: origin.kind === 'play' ? origin.playId : undefined,
  });
  return gate === 'stale-origin' ? 'stale' : gate;
}
